import fetchRetry from 'fetch-retry';
import { Map } from 'immutable';

import { TSQuestionConfig } from '@entities/Question';
import { EnvConfigs } from '@environment/resources';
import { Logger } from '@services/Logger';

export interface QuestionsConfigPayload {
  configs: TSQuestionConfig[];
}

export class QuestionsCacheService {
  private readonly StorageName = 'sentiments.questions.cache';
  private questions: Map<string, TSQuestionConfig>;

  /**
   * Initialization of Question Config service
   */
  constructor() {
    const questions = sessionStorage.getItem(this.StorageName);
    if (questions) {
      try {
        const value = JSON.parse(questions);
        if (typeof value === 'object') {
          this.questions = Map(value);
        }
      } catch (err) {
        Logger.log(`corrupted ${this.StorageName} found`, questions);
        sessionStorage.setItem(this.StorageName, '{}');
        this.questions = Map();
      }
    } else {
      // fallback
      sessionStorage.setItem(this.StorageName, '{}');
      this.questions = Map();
    }
  }

  /**
   * Build fetch url
   * @param reportSuiteId
   * @param id
   * @private
   */
  private getUrl(reportSuiteId: string, id): string {
    const search = new URLSearchParams({ ids: id });
    const paths = [EnvConfigs.QuestionsEndpoint, reportSuiteId].join('/');
    const url = new URL(paths);
    url.search = search.toString();
    return url.toString();
  }

  /**
   * Fetch questions with retry
   * @param url
   * @private
   */
  private getFetch(url: string): Promise<Map<string, TSQuestionConfig>> {
    const _fetch = fetchRetry(fetch, {
      retries: 3,
      retryDelay: 1000,
      retryOn: [400],
    });
    return _fetch(url)
      .then((response: Response) => {
        if (!response.ok) {
          throw new Error('error when fetching transactional service questions');
        }
        return response.json();
      })
      .then((payload: QuestionsConfigPayload) => {
        if (!(payload && 'configs' in payload)) {
          throw new Error('sentiments config property not found');
        }

        const itemsMap = Map(payload.configs.map(x => [x.trackingId, x]));
        this.questions = this.questions.merge(itemsMap);
        sessionStorage.setItem(this.StorageName, JSON.stringify(this.questions.toJSON()));
        return Promise.resolve(this.questions);
      });
  }

  /**
   * Merge new transactional service questions
   * @param reportSuiteId
   * @param trackingIds
   */
  sync(reportSuiteId: string, trackingIds: string[]) {
    const notInSync = this.questions.filterNot((_, y) => trackingIds.includes(y)).keySeq();
    if (notInSync.size > 0) {
      Logger.log('not yet in sync transactional service questions found', { notInSync });
      notInSync.forEach(id => {
        const url = this.getUrl(reportSuiteId, id);
        this.getFetch(url);
      });
    }
  }

  has(trackingId: string): boolean {
    return this.questions.has(trackingId);
  }

  get(trackingId: string): TSQuestionConfig {
    return this.questions.get(trackingId);
  }

  fetchIfNotExist(
    reportSuiteId: string,
    trackingId: string,
  ): Promise<Map<string, TSQuestionConfig>> {
    if (trackingId !== '' && !this.questions.has(trackingId)) {
      const url = this.getUrl(reportSuiteId, trackingId);
      return this.getFetch(url);
    }
    return Promise.resolve(this.questions);
  }
}
